import { useQueryClient } from '@tanstack/react-query';
import { NegativeToast } from 'app/components/Elements';
import { useFlagsmith } from 'flagsmith/react';
import moment from 'moment';
import { useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { authSwitchTeam, generateAuthQueryKey, useSignOut } from '../app/adapters/auth';
import { AuthSchema } from '../app/schemas';
import { invalidateCommandbar } from './commandbar';
import { invalidateDatadog } from './datadog';
import { invalidatePendo } from './pendo';

export interface ErrorPayload {
  detail: string;
  status: string;
  title: string;
}

export function syncSessionExpiryCookie(expiresAt: string | number | null | undefined) {
  if (typeof document === 'undefined') return;
  const epochSeconds = expiresAt
    ? typeof expiresAt === 'number'
      ? expiresAt
      : moment(expiresAt).unix()
    : null;
  if (!epochSeconds) {
    document.cookie = '6si-session-exp=; path=/; max-age=0';
    return;
  }
  document.cookie = `6si-session-exp=${epochSeconds}; path=/`;
}

export function success(payload: AuthSchema) {
  localStorage.setItem('auth/data', JSON.stringify(payload));
  if (payload?.session?.isAuthedWithSixSense) {
    syncSessionExpiryCookie(payload?.session?.expiresAt);
  } else {
    syncSessionExpiryCookie(null);
  }
}
export function invalidate() {
  localStorage.removeItem('auth/data');
  syncSessionExpiryCookie(null);
}

export async function switchTeam(teamId: number, redirectUrl = '/') {
  try {
    const data = await authSwitchTeam(teamId);
    success(data);
  } catch {
    invalidate();
  } finally {
    window.location.href = redirectUrl;
  }
}

export function useSelectAuthData() {
  const queryClient = useQueryClient();
  return queryClient.getQueryData<AuthSchema>(generateAuthQueryKey());
}
export function useIsAuthenticated() {
  const authData = useSelectAuthData();
  return !!authData?.session.userId;
}
export function useIsAuthenticatedWithSixSense() {
  const authData = useSelectAuthData();
  return !!authData?.session.isAuthedWithSixSense;
}
export function useSelectCurrentTeam() {
  const authData = useSelectAuthData();
  return authData?.session.currentTeam;
}
export function useSelectUserRole() {
  const authData = useSelectAuthData();
  return authData?.session.membershipRole;
}

export function useSelectPermissionKeys() {
  const authData = useSelectAuthData();
  return authData?.session.permissionKeys || [];
}

export function useSelectRbacV2Enabled() {
  const authData = useSelectAuthData();
  return !!authData?.session.rbacV2Enabled;
}

export function useHasPermission(permissionKey: string) {
  const permissionKeys = useSelectPermissionKeys();
  return permissionKeys.includes(permissionKey);
}

export function useHasAnyPermission(permissionKeys: string[]) {
  const userPermissionKeys = useSelectPermissionKeys();
  return permissionKeys.some(key => userPermissionKeys.includes(key));
}

export function useHasAllPermissions(permissionKeys: string[]) {
  const userPermissionKeys = useSelectPermissionKeys();
  return permissionKeys.every(key => userPermissionKeys.includes(key));
}
export function useSelectIsAdmin() {
  const authData = useSelectAuthData();
  return (
    authData?.session.membershipRole === 'admin' ||
    authData?.session.membershipRole === 'view_only_admin'
  );
}

// SSO url is auth/sso_login. Leaving this here for the future

export const useLogout = () => {
  const flagsmith = useFlagsmith();
  const history = useHistory();
  const queryClient = useQueryClient();
  const signOutQuery = useSignOut();
  const currentTeam = useSelectCurrentTeam();
  const isAuthenticatedWithSixSense = useIsAuthenticatedWithSixSense();
  const logoutStartedRef = useRef(false);

  return () => {
    if (!currentTeam || signOutQuery.isLoading || logoutStartedRef.current) return;
    logoutStartedRef.current = true;
    signOutQuery.mutate(undefined, {
      onSuccess: () => {
        invalidatePendo();
        invalidateCommandbar();
        invalidateDatadog();
        flagsmith.logout();
        invalidate();
        queryClient.removeQueries(generateAuthQueryKey(), { exact: true });
        if (isAuthenticatedWithSixSense) {
          const env = import.meta.env.VITE_APP_ENV;
          const url =
            env === 'prod'
              ? `https://${currentTeam.sixSenseOrgName}.abm.6sense.com/logout/`
              : env === 'staging'
              ? `https://${currentTeam.sixSenseOrgName}.account-analytics-release.dev.6si.com/logout/`
              : `https://${currentTeam.sixSenseOrgName}.account-analytics.dev.6si.com/logout/`;
          window.location.href = url;
        } else {
          logoutStartedRef.current = false;
          history.push('/');
        }
      },
      onError: () => {
        logoutStartedRef.current = false;
        NegativeToast('An error occured while logging out.');
      },
    });
  };
};
